import { DOCUMENT } from '@angular/common'
import { inject, Injectable } from '@angular/core'
import { Meta, Title } from '@angular/platform-browser'
import { AppConfigService } from '@mos/client/shared/app-config'

export interface SeoData {
	title: string
	description: string
	ogTitle?: string
}

@Injectable({ providedIn: 'root' })
export class SeoService {
	private readonly document = inject(DOCUMENT)
	private readonly metaService = inject(Meta)
	private readonly titleService = inject(Title)
	private readonly appConfigService = inject(AppConfigService)

	// Sets the site-wide defaults (title, description, Open Graph, Twitter Card). Values are
	// $localize strings so they're baked in correctly per locale for prerendered routes, and
	// corrected client-side on navigation for the others.
	set(data: SeoData): void {
		const siteUrl = this.appConfigService.getSiteUrl()
		const ogTitle = data.ogTitle ?? data.title
		// Matches each game's asset filename convention (memoryofsouls.jpg, ashesandroses.jpg, ...):
		// displayName with spaces stripped. Per-game, so a new game just needs the matching asset
		// dropped at that path - no code change here.
		const imageSlug = this.appConfigService.getDisplayName().replace(/\s+/g, '').toLowerCase()
		const image = `${siteUrl}/assets/images/meta/${imageSlug}.jpg`

		this.titleService.setTitle(data.title)
		this.metaService.updateTag({ name: 'description', content: data.description })
		this.metaService.updateTag({ property: 'og:url', content: `${siteUrl}/` })
		this.metaService.updateTag({ property: 'og:title', content: ogTitle })
		this.metaService.updateTag({ property: 'og:description', content: data.description })
		this.metaService.updateTag({ property: 'og:image', content: image })
		this.metaService.updateTag({ property: 'twitter:title', content: ogTitle })
		this.metaService.updateTag({ property: 'twitter:description', content: data.description })
		this.metaService.updateTag({ property: 'twitter:image', content: image })
	}

	// Overrides description/og:description/twitter:description for the lifetime of the caller
	// (e.g. a specific page). Returns a cleanup function restoring whatever value was set before
	// this call, so nesting/leaving the override behaves correctly regardless of call order.
	setDescription(description: string): () => void {
		const previous = {
			description: this.metaService.getTag('name="description"')?.content,
			ogDescription: this.metaService.getTag('property="og:description"')?.content,
			twitterDescription: this.metaService.getTag('property="twitter:description"')?.content,
		}

		this.metaService.updateTag({ name: 'description', content: description })
		this.metaService.updateTag({ property: 'og:description', content: description })
		this.metaService.updateTag({ property: 'twitter:description', content: description })

		return () => {
			if (previous.description !== undefined) {
				this.metaService.updateTag({ name: 'description', content: previous.description })
			}
			if (previous.ogDescription !== undefined) {
				this.metaService.updateTag({ property: 'og:description', content: previous.ogDescription })
			}
			if (previous.twitterDescription !== undefined) {
				this.metaService.updateTag({ property: 'twitter:description', content: previous.twitterDescription })
			}
		}
	}

	// Sets <link rel="canonical">, creating it if absent. Returns a cleanup function the caller
	// should run on destroy so the tag doesn't linger on routes it doesn't apply to.
	setCanonicalUrl(url: string): () => void {
		let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
		if (!link) {
			link = this.document.createElement('link')
			link.setAttribute('rel', 'canonical')
			this.document.head.appendChild(link)
		}
		link.setAttribute('href', url)

		return () => link?.remove()
	}

	// Sets a <script type="application/ld+json"> structured-data block, identified by `id` so
	// multiple independent blocks (FAQPage, VideoGame, ...) can coexist without clashing. Returns
	// a cleanup function the caller should run on destroy so it doesn't linger on other routes.
	setJsonLd(id: string, data: unknown): () => void {
		const selector = `script[type="application/ld+json"][data-seo-id="${id}"]`
		let script = this.document.querySelector<HTMLScriptElement>(selector)
		if (!script) {
			script = this.document.createElement('script')
			script.type = 'application/ld+json'
			script.setAttribute('data-seo-id', id)
			this.document.head.appendChild(script)
		}
		script.textContent = JSON.stringify(data)

		return () => script?.remove()
	}
}
